<template>
  <v-dialog
    v-model="show"
    max-width="500px"
    >
      <v-card>
        <v-card-title>
          Request For Absence
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click.prevent="show = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-row>
            <!-- Request for -->
            <v-col cols="12" md="6">
              <v-select
                hide-details
                v-model="requestFor"
                :items="requestForItems"
                label="Request For"
                @change="toDate = undefined; toTime = undefined; fromTime = undefined"
              >
              </v-select>
            </v-col>
            <!-- /Request for -->
            <!-- Employee List -->
            <v-col cols="12" md="6">
              <v-select
                hide-details
                v-model="notificationData.Notification_Employee_ID_To"
                :items="employeeList"
                label="Request To"
                item-text="full_name"
                item-value="employee_id"
              >
                <template v-slot:prepend-item>
                  <v-container>
                    <v-row>
                      <v-col>
                        <v-text-field
                          dense
                          hide-details
                          label="Search employee"
                          v-model="searchEmployee"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                  </v-container>
                </template>
              </v-select>
            </v-col>
            <!-- /Employee List -->
          </v-row>
          <v-row v-if="requestFor === 'Sick leave'">
            <v-col cols="12">
              <v-textarea
                v-model="sickLeaveReason"
                label="Illness Reason"
                :rules="[rules.required, rules.englishOnly]"
                auto-grow
                rows="2"
                :counter="500"
                :maxlength="500"
                @input="validateSickLeaveReason"
              />
            </v-col>
          </v-row>
          <v-row v-if="requestFor">
            <!-- Date From -->
            <v-col cols="12" md="6">
              <DatePicker
                v-model="fromDate"
                label="Start Date"
                :max="showEndDate ? toDate : undefined"
                :allowedDates="allowedDates"
              />
            </v-col>
            <!-- /Date From -->
            <!-- Date To -->
            <v-col cols="12" md="6" v-if="showEndDate">
              <DatePicker
                v-model="toDate"
                label="End Date"
                :min="fromDate"
                :allowedDates="allowedDates"
              />
            </v-col>
            <!-- /Date To -->
          </v-row>
          <v-row v-if="showTimepickers">
            <!-- Time from -->
            <v-col cols="12" md="6">
              <v-menu
                ref="menuTimeFrom"
                v-model="menuTimeFrom"
                :close-on-content-click="false"
                :nudge-right="40"
                :return-value.sync="fromTime"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="fromTime"
                    label="Time From"
                    prepend-icon="mdi-clock-time-four-outline"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-time-picker
                  format="24hr"
                  min="10:00"
                  :max="maxHours"
                  :allowed-minutes="allowedMinutes"
                  v-if="menuTimeFrom"
                  v-model="fromTime"
                  full-width
                  @click:minute="$refs.menuTimeFrom.save(fromTime)"
                ></v-time-picker>
              </v-menu>
            </v-col>
            <!-- /Time from -->
            <!-- Time to -->
            <v-col cols="12" md="6" v-if="showEndTimepicker">
              <v-menu
                ref="menuTimeTo"
                v-model="menuTimeTo"
                :close-on-content-click="false"
                :nudge-right="40"
                :return-value.sync="toTime"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="toTime"
                    label="Time To"
                    prepend-icon="mdi-clock-time-four-outline"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-time-picker
                  format="24hr"
                  :min="fromTime || '10:00'"
                  max="19:00"
                  :allowed-minutes="allowedMinutes"
                  v-if="menuTimeTo"
                  v-model="toTime"
                  full-width
                  @click:minute="$refs.menuTimeTo.save(toTime)"
                ></v-time-picker>
              </v-menu>
            </v-col>
            <!-- /Time to -->
          </v-row>
          <v-row v-if="requestFor === 'Sick leave'">
            <v-col cols="12" md="6" lg="6">
              <v-file-input
                label="Confirmation File"
                v-model="confirmationFile"
                @change="onConfirmationCheckFileChange"
                show-size
                counter
                :accept="ALLOWED_FILE_TYPES"
              ></v-file-input>
              <div
                v-if="requestId && confirmationFileName && !isNewConfirmationFile"
                class="text-caption text--secondary mt-n2 mb-2"
              >
                Current file: {{ confirmationFileName }}
              </div>
            </v-col>
            <v-col cols="12" md="6" lg="6">
              <v-select
                v-model="selectedAmountSickDays"
                label="Selected Days"
                :items="getAvailableAmountSickDays"
              >
                <template v-slot:prepend>
                  <v-tooltip bottom max-width="300px" color="#424852">
                    <template v-slot:activator="{ on }">
                      <v-icon
                        v-on="on"
                        color="primary"
                        style="cursor:pointer;"
                      >
                        mdi-help-circle-outline
                      </v-icon>
                    </template>
                    Full range is <b>{{ getAvailableAmountSickDays[0] }}</b>
                    {{ periodStringFor(getAvailableAmountSickDays[0]) }}.
                    You can also choose <b>{{ getAvailableAmountSickDays[1] }}</b>
                    (last day half)
                    <template v-if="getAvailableAmountSickDays.length > 2">
                      or <b>{{ getAvailableAmountSickDays[2] }}</b>
                      (0.5 each weekday)
                    </template>.
                  </v-tooltip>
                </template>
              </v-select>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn small :loading="isLoading" :disabled="!isRequestReady || !isRequestAppropriate || isLoading" color="primary" @click="sendRequest">Send</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
</template>
<script>
import moment from 'moment'
import { ALLOWED_FILE_TYPES } from '@/constants/fileAccept'
import { getApiErrorMessage } from '@/utils/apiError'
import { fileToDataUrl } from '@/utils/fileDataUrl'

export default {
  name: 'AbsenceRequest',
  components: {
    DatePicker: () => import('@/components/global/DatePicker.vue')
  },
  props: {
    value: {
      required: true,
      default: false
    },
    requestId: {
      type: Number,
      default: null
    }
  },
  data () {
    return {
      showCancelDialog: false,
      isRequestAppropriate: true,
      isProductionEnv: process.env.NODE_ENV === 'production',
      menuTimeFrom: false,
      menuTimeTo: false,
      notificationsTypes: [],
      notificationData: {
        Notification_Type_ID: 1,
        Notification_Title: '',
        Notification_Message: '',
        Notification_Created: undefined,
        Notification_Employee_ID_From: undefined,
        Notification_Employee_ID_To: undefined
      },
      requestFor: undefined,
      requestForItems: ['Vacation', 'Half day vacation', 'Step out', 'Sick leave'],
      fromDate: undefined,
      fromTime: undefined,
      toDate: undefined,
      toTime: undefined,
      searchEmployee: '',
      selectedAmountSickDays: undefined,
      sickLeaveReason: '',
      confirmationFileName: '',
      confirmationFileType: '',
      confirmationFile: null,
      isNewConfirmationFile: false,
      isLoading: false,
      hasCyrillic: false,
      ALLOWED_FILE_TYPES,
      rules: {
        required: value => !!(value && value.toString().trim().length) || 'Reason is required',
        englishOnly: value => {
          if (!value) return true
          const hasCyrillic = /[а-яА-ЯЁёҐґЄєІіЇї]/.test(value)
          return !hasCyrillic || 'English only'
        }
      }
    }
  },
  computed: {
    show: {
      get () {
        return this.value
      },
      set (value) {
        this.$emit('input', value)
        if (!value) {
          this.clearAll()
          this.isRequestAppropriate = true
        }
      }
    },
    employeeList () {
      if (this.searchEmployee) {
        const reg = new RegExp(this.searchEmployee.toLowerCase(), 'gi')
        return this.$store.getters.getEmployee
          .filter(item => this.isProductionEnv ? item.employee_id !== this.user.employee_id : item)
          .filter(item => {
            reg.lastIndex = 0
            return reg.test(item.full_name.toLowerCase())
          })
      } else return this.$store.getters.getEmployee.filter(item => this.isProductionEnv ? item.employee_id !== this.user.employee_id : item)
    },
    getAvailableAmountSickDays () {
      if (this.fromDate && this.toDate) {
        const fromDate = moment(this.fromDate)
        const toDate = moment(this.toDate)
        const diffInDays = toDate.diff(fromDate, 'days')
        let businessDaysDiff = 0

        for (let i = 0; i <= diffInDays; i++) {
          const currentDay = fromDate.clone().add(i, 'days')

          if (this.allowedDates(currentDay)) {
            businessDaysDiff++
          }
        }
        if (businessDaysDiff < 1) return []
        const options = [businessDaysDiff]
        const lastHalf = businessDaysDiff - 0.5
        if (lastHalf > 0) options.push(lastHalf)
        const allHalf = businessDaysDiff * 0.5
        if (allHalf > 0 && options.indexOf(allHalf) === -1) {
          options.push(allHalf)
        }
        return options
      } else {
        return []
      }
    },
    periodString () {
      return this.periodStringFor(this.selectedAmountSickDays)
    },
    maxHours () {
      return this.requestFor === 'Half day vacation' ? '15:00' : '19:00'
    },
    showEndDate () {
      return this.requestFor && ['Vacation', 'Sick leave', 'Half day vacation'].indexOf(this.requestFor) > -1
    },
    showEndTimepicker () {
      return this.fromTime && this.requestFor && this.requestFor !== 'Half day vacation'
    },
    showTimepickers () {
      return this.requestFor && ['Vacation', 'Sick leave'].indexOf(this.requestFor) === -1
    },
    hasSickLeaveReason () {
      return !!(this.sickLeaveReason &&
                this.sickLeaveReason.toString().trim().length &&
                !this.hasCyrillic)
    },
    sickLeaveReasonText () {
      return this.sickLeaveReason ? this.sickLeaveReason.toString().trim() : ''
    },
    user () {
      return this.$store.getters.getUser
    },
    notifications () {
      return this.$store.getters.getNotifications
    },
    isRequestReady () {
      let condition = false
      if (this.requestFor === 'Vacation' || this.requestFor === 'Sick leave') {
        condition = this.fromDate && this.toDate
        if (this.requestFor === 'Sick leave') {
          condition = condition && this.hasSickLeaveReason
        }
      }
      if (this.requestFor === 'Half day vacation') {
        condition = this.fromDate && this.toDate && this.fromTime
      }
      if (this.requestFor === 'Step out') {
        condition = this.fromDate && this.fromTime && this.toTime
      }
      return condition && this.notificationData.Notification_Employee_ID_To
    }
  },
  mounted () {
    this.getEmployeeList()
    if (this.requestId) {
      this.getAbsenceRequest(this.requestId)
    }
  },
  watch: {
    getAvailableAmountSickDays: {
      handler (newVal) {
        if (newVal.length > 0 && this.selectedAmountSickDays == null) {
          this.selectedAmountSickDays = newVal[0]
        }
      }
    },
    fromDate (val) {
      if (this.requestFor === 'Half day vacation' && val) {
        if (!this.toDate || this.toDate < val) {
          this.toDate = val
        }
      }
    }
  },
  methods: {
    buildNotificationPayload () {
      if (this.requestFor === 'Vacation') {
        this.notificationData.Notification_Message =
          `${this.user.full_name} wants to take ${this.requestFor.toLowerCase()} from ${this.fromDate} to ${this.toDate}`
      }
      if (this.requestFor === 'Sick leave') {
        this.notificationData.Notification_Message =
          `${this.user.full_name} wants to take ${this.requestFor.toLowerCase()} from ${this.fromDate} to ${this.toDate} for ${this.selectedAmountSickDays} ${this.periodString}. Reason: ${this.sickLeaveReasonText}`
      }
      if (this.requestFor === 'Half day vacation' || this.requestFor === 'Step out') {
        const from = new Date(`${this.fromDate} ${this.fromTime}`)
        const to = new Date(from.getTime() + (4 * 60 * 60 * 1000))
        this.toTime = this.toTime || `${to.getHours().toString().padStart(2, '0')}:${to.getMinutes().toString().padStart(2, '0')}`
        this.notificationData.Notification_Message =
          `${this.user.full_name} wants to take ${this.requestFor.toLowerCase()} ${this.halfDayPeriodMessage()}`
      }
      this.notificationData.Notification_Employee_ID_From = this.user.employee_id
      this.notificationData.Notification_Title = `Request for ${this.requestFor.toLowerCase()}`
      this.notificationData.Notification_Created = new Date().toISOString()
    },
    buildRequestPayload (notificationID) {
      let period = ''
      if (this.requestFor === 'Vacation') {
        period = `Period: from ${this.fromDate} to ${this.toDate}`
      }
      if (this.requestFor === 'Sick leave') {
        period = `Period: from ${this.fromDate} to ${this.toDate} for ${this.selectedAmountSickDays} ${this.periodString}`
      }
      if (this.requestFor === 'Half day vacation' || this.requestFor === 'Step out') {
        period = `Period: ${this.halfDayPeriodMessage()}`
      }
      const descriptionLines = [
        `Request for ${this.requestFor.toLowerCase()}`,
        period
      ]
      if (this.requestFor === 'Sick leave' && this.sickLeaveReasonText) {
        descriptionLines.push(`Reason: ${this.sickLeaveReasonText}`)
      }
      const endDate = this.toDate || this.fromDate || null
      const requestData = {
        Description: descriptionLines.filter(Boolean).join('\n'),
        Creator_ID: this.notificationData.Notification_Employee_ID_From || this.user.employee_id,
        Confirmer_ID: this.notificationData.Notification_Employee_ID_To,
        Confirmation_File_Name: this.confirmationFileName,
        Confirmation_File_Type: this.confirmationFileType,
        Confirmation_File: this.confirmationFile,
        Notification_ID: notificationID,
        Start_Date: this.fromDate || null,
        End_Date: endDate,
        Duration: this.requestFor === 'Sick leave' && this.selectedAmountSickDays
          ? this.selectedAmountSickDays
          : null,
        isNewConfirmationFile: this.isNewConfirmationFile
      }
      return requestData
    },
    createAbsenceNotification () {
      this.buildNotificationPayload()
      return this.axios.post('/api/notifications', this.notificationData, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      })
    },
    sendRequest () {
      this.isLoading = true
      this.isRequestAppropriate = false
      const headers = {
        Authorization: 'Bearer ' + this.user.token
      }
      const onError = (error) => {
        this.isRequestAppropriate = true
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      }

      if (this.requestId) {
        const payload = this.buildRequestPayload(null)
        this.axios.put(`/api/requests/absence_request/${this.requestId}`, payload, { headers })
          .then((res) => {
            if (!res.data || !res.data[0] || !res.data[0].Notification_ID) {
              return Promise.reject(new Error('Request is not pending or was not found'))
            }
            this.$store.dispatch('setSuccess', { successMsg: 'Absence request details were updated.' })
            return this.axios.delete(`/api/notifications/${res.data[0].Notification_ID}`, { headers })
          })
          .then(() => {
            return this.createAbsenceNotification()
          })
          .then((newNotificationResponse) => {
            const newNotificationId = newNotificationResponse.data[0].Notification_ID
            return this.axios.put(
              `/api/requests/update_notification_absence_request/${this.requestId}`,
              { Notification_ID: newNotificationId },
              { headers }
            )
          })
          .then(() => {
            if (this.requestFor === 'Sick leave') {
              this.sendEmailForSickLeaveRequest(this.requestId)
            }
            this.show = false
            this.$emit('getHistory')
            this.$store.dispatch('setSuccess', { successMsg: 'Absence request was fully updated.' })
          })
          .catch(onError)
          .finally(() => {
            this.isLoading = false
          })
      } else {
        this.createAbsenceNotification()
          .then(response => {
            this.notificationData.Notification_ID = response.data[0].Notification_ID
            return this.postAbsenceRequest(response.data[0].Notification_ID)
          })
          .then(response => {
            if (this.requestFor === 'Sick leave') {
              this.sendEmailForSickLeaveRequest(response.data[0].Request_ID)
            }
            this.show = false
            this.$store.dispatch('setSuccess', { successMsg: 'Request successfully sent' })
          })
          .catch(onError)
          .finally(() => {
            this.isLoading = false
          })
      }
    },
    getAbsenceRequest (requestId) {
      this.axios.get(`/api/requests/absence_by_id/${requestId}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        if (res.data && res.data[0]) {
          this.hydrateFromRequest(res.data[0])
          if (res.data[0].Confirmation_File_Name) {
            this.getConfirmationFile(requestId)
          }
        }
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    getConfirmationFile (requestId) {
      this.axios.get(`/api/requests/absence/confimation_file/${requestId}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(async res => {
        if (!res.data || !res.data[0] || !res.data[0].Confirmation_File || !res.data[0].Confirmation_File.data) {
          return
        }
        const fileName = this.confirmationFileName || res.data[0].Confirmation_File_Name
        const fileType = this.confirmationFileType || res.data[0].Confirmation_File_Type
        const byteArray = new Uint8Array(res.data[0].Confirmation_File.data)
        const restoredFile = new File([byteArray], fileName, { type: fileType })
        this.confirmationFile = restoredFile
        await this.onFileChange(restoredFile)
        this.isNewConfirmationFile = false
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    hydrateFromRequest (data) {
      const desc = data.Description || ''
      const lines = desc.split('\n').map(line => line.trim()).filter(Boolean)
      const titleLine = (lines[0] || '').toLowerCase()
      const typeMap = {
        'request for vacation': 'Vacation',
        'request for sick leave': 'Sick leave',
        'request for half day vacation': 'Half day vacation',
        'request for step out': 'Step out'
      }
      this.requestFor = typeMap[titleLine] || undefined
      this.notificationData.Notification_Employee_ID_To = data.Confirmer_ID

      if (data.Start_Date) this.fromDate = data.Start_Date
      if (data.End_Date) this.toDate = data.End_Date
      if (data.Duration != null) this.selectedAmountSickDays = data.Duration

      const periodLine = lines.find(line => /^Period:/i.test(line)) || ''
      const reasonLine = lines.find(line => /^Reason:/i.test(line))
      if (reasonLine) {
        this.sickLeaveReason = reasonLine.replace(/^Reason:\s*/i, '')
        this.validateSickLeaveReason()
      }

      const multiHalf = periodLine.match(
        /from (\d{4}-\d{2}-\d{2}) to (\d{4}-\d{2}-\d{2}),\s*(\d{2}:\d{2})[–-](\d{2}:\d{2})/i
      )
      const singleTimed = periodLine.match(
        /from (\d{2}:\d{2}) to (\d{2}:\d{2}) on (\d{4}-\d{2}-\d{2})/i
      )
      const dateRange = periodLine.match(
        /from (\d{4}-\d{2}-\d{2}) to (\d{4}-\d{2}-\d{2})/i
      )
      const durationMatch = periodLine.match(/for ([\d.]+) day/i)

      if (multiHalf) {
        this.fromDate = this.fromDate || multiHalf[1]
        this.toDate = this.toDate || multiHalf[2]
        this.fromTime = multiHalf[3]
        this.toTime = multiHalf[4]
      } else if (singleTimed) {
        this.fromTime = singleTimed[1]
        this.toTime = singleTimed[2]
        this.fromDate = this.fromDate || singleTimed[3]
        // Step out has no End Date field — do not set toDate (it would cap Start Date via :max)
        if (this.requestFor !== 'Step out') {
          this.toDate = this.toDate || singleTimed[3]
        }
      } else if (dateRange) {
        this.fromDate = this.fromDate || dateRange[1]
        this.toDate = this.toDate || dateRange[2]
      }

      if (durationMatch && this.selectedAmountSickDays == null) {
        this.selectedAmountSickDays = parseFloat(durationMatch[1])
      }

      if (data.Confirmation_File_Name) {
        this.confirmationFileName = data.Confirmation_File_Name
        this.confirmationFileType = data.Confirmation_File_Type || ''
      }
      this.isNewConfirmationFile = false

      // Step out UI has no End Date; keep toDate unset so Start Date is not constrained
      if (this.requestFor === 'Step out') {
        this.toDate = undefined
      }
    },
    clearAll () {
      this.requestFor = undefined
      this.fromDate = undefined
      this.fromTime = undefined
      this.toDate = undefined
      this.toTime = undefined
      this.searchEmployee = ''
      this.selectedAmountSickDays = undefined
      this.sickLeaveReason = ''
      this.hasCyrillic = false
      this.notificationData.Notification_Employee_ID_To = undefined
      this.confirmationFileName = ''
      this.confirmationFileType = ''
      this.confirmationFile = null
      this.isNewConfirmationFile = false
    },
    periodStringFor (amount) {
      return amount <= 1 ? 'day' : 'days'
    },
    halfDayPeriodMessage () {
      if (this.requestFor === 'Half day vacation' && this.toDate && this.toDate !== this.fromDate) {
        return `from ${this.fromDate} to ${this.toDate}, ${this.fromTime}–${this.toTime} each weekday (0.5 day)`
      }
      return `from ${this.fromTime} to ${this.toTime} on ${this.fromDate}`
    },
    allowedDates (date) {
      const dayOfTheWeek = moment(date).format('dd').toLowerCase()
      return ['sa', 'su'].indexOf(dayOfTheWeek) === -1
    },
    allowedMinutes (m) {
      return m === 30 || m === 0
    },
    getEmployeeList () {
      if (!this.employeeList.length) {
        this.axios.get('/api/employeelist', {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(response => {
          this.$store.dispatch('setEmployeeList', { employeeList: response.data })
        }).catch(error => {
          const errorMsg = getApiErrorMessage(error)
          this.$store.dispatch('setError', { errorMsg })
        })
      }
    },
    postAbsenceRequest (notificationID) {
      const requestData = this.buildRequestPayload(notificationID)
      return this.axios.post('/api/requests/absence', requestData,
        {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
    },
    validateSickLeaveReason () {
      if (this.sickLeaveReason) {
        this.hasCyrillic = /[а-яА-ЯЁёҐґЄєІіЇї]/.test(this.sickLeaveReason)
      } else {
        this.hasCyrillic = false
      }
    },
    onConfirmationCheckFileChange (e) {
      this.onFileChange(e)
    },
    async onFileChange (e) {
      if (!e) {
        this.confirmationFileName = ''
        this.confirmationFileType = ''
        this.confirmationFile = null
        // Mark as changed so edit PUT clears DB/S3 file (same idea as Refund)
        this.isNewConfirmationFile = Boolean(this.requestId)
        return
      }
      try {
        const res = await fileToDataUrl(e)
        this.confirmationFileName = e.name
        this.confirmationFileType = e.type
        if (this.confirmationFile && typeof this.confirmationFile === 'object') {
          this.confirmationFile.data = res.data
          this.confirmationFile.base64String = res.base64String
        }
        this.isNewConfirmationFile = true
      } catch (error) {
        this.confirmationFileName = ''
        this.confirmationFileType = ''
        this.confirmationFile = null
        this.isNewConfirmationFile = false
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      }
    },
    sendEmailForSickLeaveRequest (requestId) {
      const body = {
        requestId: requestId
      }
      this.axios.post('/api/emails/sendemailforsickleaverequest', body, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.$store.dispatch('setSuccess', { successMsg: 'Email sent' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    }
  }
}
</script>
